import React, { useState, useEffect, useRef } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useLazyQuery } from "@apollo/client";
import { FiInfo, FiMusic, FiHelpCircle, FiExternalLink } from "react-icons/fi";
import { HiPlay } from "react-icons/hi2";

import theme from "theme/themeThree";
import { Button } from "MVVM/Components/Button";
import { InputText } from "MVVM/Components/Inputs";
import { STUDIO_QUERY } from "MVVM/GraphQL/studioFacet";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import { ReactComponent as ProIcon } from "../../../../assets/images/pro/new/PRO-icon.svg";
import { ReactComponent as StudioBadgeIcon } from "../../../../assets/images/badges/studio_icon.svg";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { BaseSection } from "../styledComponents";
import { IQuickEditMoment } from "../@types";
import { CompactCardGrid } from "MVVM/Views/Studio/Components/MainRail/Components/CardGrid";
import { usePortal } from "MVVM/Hooks/usePortal";
import { SubscriptionPaywall } from "MVVM/Components/Modals/SubscriptionPaywall";
import { analytics } from "MVVM/Analytics";

const { REACT_APP_S3_STATIC } = process.env;

interface IAmsCard {
  _id: string;
  ui: {
    title: string;
    image: string;
    hoverEffectSrc?: string;
  };
  facet: {
    theme: string[];
  };
}

interface ITimeRemapProps {
  moment: IQuickEditMoment;
  onBack: () => void;
  onCreateTimeRemap: (params: ITimeRemapParams) => Promise<void>;
  title: string;
  setTitle: (title: string) => void;
  closeModal: () => void;
  allFeaturesLocked: boolean;
}

export interface ITimeRemapParams {
  title: string;
  selectedSong: IAmsCard | null;
  songVolume: number;
  impactSeverity: number;
  speedupVfxEnabled: boolean;
  applyStudioCards: boolean;
}

type TTimeRemapStep = "settings" | "song-selection";

export const TimeRemap = ({
  moment,
  onBack,
  onCreateTimeRemap,
  title,
  setTitle,
  closeModal,
  allFeaturesLocked,
}: ITimeRemapProps) => {
  const [step, setStep] = useState<TTimeRemapStep>("settings");
  const [selectedSong, setSelectedSong] = useState<IAmsCard | null>(null);
  const [songVolume, setSongVolume] = useState<number>(40);
  const [impactSeverity, setImpactSeverity] = useState<number>(30); // 0-100, maps to 0.0-1.0
  const [speedupVfxEnabled, setSpeedupVfxEnabled] = useState<boolean>(true);
  const [applyStudioCards, setApplyStudioCards] = useState<boolean>(false);
  const [showStudioCardsInfo, setShowStudioCardsInfo] =
    useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [themeFilter, setThemeFilter] = useState<string>("");
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [showSongError, setShowSongError] = useState<boolean>(false);

  const {
    portalOpen: subscriptionPaywallOpen,
    setPortalOpen: setSubscriptionPaywallOpen,
    Portal: SubscriptionPaywallPortal,
  } = usePortal(
    <SubscriptionPaywall
      featureName="Instant Fragmovie"
      analyticsName="Time Remap"
      requiredTierKey="ESSENTIALS"
      onClose={() => setSubscriptionPaywallOpen(false)}
    />,
  );

  const [fetchSongs, { data: songsData, loading: songsLoading }] =
    useLazyQuery(STUDIO_QUERY);

  useEffect(() => {
    if (step === "song-selection") {
      fetchSongs({
        variables: {
          type: ["Music"],
          theme: themeFilter ? [themeFilter] : undefined,
        },
      });
    }
  }, [step, themeFilter, fetchSongs]);

  const songs: IAmsCard[] = songsData?.facets?.cards || [];
  const themeFacets = songsData?.facets?.facets?.find(
    (f: { identifier: string }) => f.identifier === "theme",
  );

  const filteredSongs = songs.filter((song) =>
    song.ui.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleCreateTimeRemap = async () => {
    if (!selectedSong) {
      setShowSongError(true);
      return;
    }
    setShowSongError(false);

    if (allFeaturesLocked) {
      analytics.track("Time Remap - Paywall Shown", {
        location: "Quick Edit - Time Remap",
      });
      setSubscriptionPaywallOpen(true);
      return;
    }

    setIsCreating(true);
    try {
      await onCreateTimeRemap({
        title,
        selectedSong,
        songVolume,
        impactSeverity: impactSeverity / 100, // Convert to 0.0-1.0
        speedupVfxEnabled,
        applyStudioCards,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const [hoveredSongId, setHoveredSongId] = useState<string | null>(null);
  const [pendingSelection, setPendingSelection] = useState<IAmsCard | null>(
    null,
  );

  const handleSongSelect = (song: IAmsCard) => {
    setPendingSelection(song);
  };

  const handleChooseSong = () => {
    if (pendingSelection) {
      setSelectedSong(pendingSelection);
      setPendingSelection(null);
      setShowSongError(false);
      setStep("settings");
    }
  };

  if (step === "song-selection") {
    return (
      <>
        <SongSelectionContainer>
          <SongSelectionHeader>
            <Text variant="text.headingXSmall">Select a Song</Text>
          </SongSelectionHeader>

          <SearchAndFilterRow>
            <SearchInput
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              placeholder="Search songs..."
            />
            {themeFacets && (
              <ThemeFilterSelect
                value={themeFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setThemeFilter(e.target.value)
                }
              >
                <option value="">All Themes</option>
                {themeFacets.entries?.map(
                  (entry: { label: string; count: number }) => (
                    <option key={entry.label} value={entry.label}>
                      {entry.label} ({entry.count})
                    </option>
                  ),
                )}
              </ThemeFilterSelect>
            )}
          </SearchAndFilterRow>

          <SongsGridWrapper>
            {songsLoading ? (
              <LoadingContainer>
                <LoadingSpinner height={30} width={30} />
              </LoadingContainer>
            ) : filteredSongs.length === 0 ? (
              <NoSongsText>No songs found</NoSongsText>
            ) : (
              <CompactCardGrid
                cards={filteredSongs}
                selectedCardId={pendingSelection?._id}
                onCardSelect={handleSongSelect}
                hoveredCardId={hoveredSongId}
                onCardHover={setHoveredSongId}
              />
            )}
          </SongsGridWrapper>

          <SongSelectionFooter>
            <FooterLeftSection>
              <Button
                classification="roundedFilledAlt"
                size="small"
                onClick={() => {
                  setPendingSelection(null);
                  setStep("settings");
                }}
                flex={true}
              >
                Back
              </Button>
              <SelectionIndicator>
                {pendingSelection ? (
                  <>
                    Selected:{" "}
                    <SelectedSongTitle>
                      {pendingSelection.ui.title}
                    </SelectedSongTitle>
                  </>
                ) : (
                  <NoSelectionText>No song selected</NoSelectionText>
                )}
              </SelectionIndicator>
            </FooterLeftSection>
            <Button
              classification="roundedFilled"
              size="small"
              onClick={handleChooseSong}
              disabled={!pendingSelection}
              flex={true}
            >
              Choose Song
            </Button>
          </SongSelectionFooter>
        </SongSelectionContainer>
        {subscriptionPaywallOpen && SubscriptionPaywallPortal}
      </>
    );
  }

  return (
    <>
      <SettingsContainer>
        <HeaderSection>
          <Flex alignItems="center" style={{ gap: 8 }}>
            <Text variant="text.headingXSmall">Instant Fragmovie</Text>
            <StyledProBadge />
          </Flex>
          <WhatIsThisButton onClick={() => setShowPreview(true)}>
            <FiHelpCircle size={14} />
            What is this?
          </WhatIsThisButton>
        </HeaderSection>

        {showPreview && (
          <PreviewModal>
            <PreviewContent>
              <PreviewCloseButton
                onClick={() => {
                  setShowPreview(false);
                  setIsVideoPlaying(false);
                  if (videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.currentTime = 0;
                  }
                }}
              >
                ×
              </PreviewCloseButton>
              <Text variant="text.headingXSmall" mb={3}>
                What is Instant Fragmovie
              </Text>
              <PreviewVideoContainer>
                <PreviewVideo
                  ref={videoRef}
                  src={`${REACT_APP_S3_STATIC}/static/web/whatIsTimeRemap.mp4`}
                  loop
                  playsInline
                  onClick={() => {
                    if (videoRef.current) {
                      if (videoRef.current.paused) {
                        videoRef.current.play();
                        setIsVideoPlaying(true);
                      } else {
                        videoRef.current.pause();
                        setIsVideoPlaying(false);
                      }
                    }
                  }}
                  onEnded={() => setIsVideoPlaying(false)}
                />
                {!isVideoPlaying && (
                  <PlayIconOverlay
                    onClick={() => {
                      if (videoRef.current) {
                        videoRef.current.play();
                        setIsVideoPlaying(true);
                      }
                    }}
                  >
                    <HiPlay size={48} />
                  </PlayIconOverlay>
                )}
              </PreviewVideoContainer>
              <PreviewDescription>
                Instant Fragmovie creates a cinematic edit of your clip with
                time warping effects synced to music. The clip will slow down
                during key moments and speed up between them, creating a
                professional fragmovie look with minimal effort.
              </PreviewDescription>
            </PreviewContent>
          </PreviewModal>
        )}

        <SettingsSection>
          <SettingGroup>
            <SettingLabel>Clip Title</SettingLabel>
            <StyledInput
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
              placeholder="Enter a custom clip title..."
            />
          </SettingGroup>

          <SettingGroup>
            <ToggleRow>
              <ToggleLabel>
                <ToggleCheckbox
                  type="checkbox"
                  checked={applyStudioCards}
                  onChange={(e) => setApplyStudioCards(e.target.checked)}
                  id="studio-cards"
                />
                <ToggleSlider />
                <ToggleLabelText>
                  <StyledStudioIcon />
                  Use Studio cards
                </ToggleLabelText>
              </ToggleLabel>
              <ToggleActions>
                <InfoIconButton onClick={() => setShowStudioCardsInfo(true)}>
                  <FiHelpCircle size={14} />
                </InfoIconButton>
                <GoToStudioLink
                  href="/studio"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiExternalLink size={12} />
                  Go to Studio
                </GoToStudioLink>
              </ToggleActions>
            </ToggleRow>
          </SettingGroup>

          {showStudioCardsInfo && (
            <StudioCardsInfoModal>
              <StudioCardsInfoContent>
                <PreviewCloseButton
                  onClick={() => setShowStudioCardsInfo(false)}
                >
                  ×
                </PreviewCloseButton>
                <Text variant="text.headingXSmall" mb={3}>
                  Use Studio Cards
                </Text>
                <StudioCardsInfoDescription>
                  <p>
                    Enabling this option will try to apply any cards you have
                    selected in Studio. Some cards are not applicable.
                  </p>
                  <p>
                    You can go to Studio, select your cards and come back here
                    to create an Instant Fragmovie with the cards you selected
                    in Studio.
                  </p>
                  <p>
                    Here is the overview of which cards are compatible and which
                    are not:
                  </p>
                  <StudioCardsListTitle>Applicable cards:</StudioCardsListTitle>
                  <StudioCardsList>
                    <li>Intros / Outros</li>
                    <li>ReShade</li>
                    <li>In-game asset replacements</li>
                    <li>CS2 configuration cards</li>
                    <li>Watermarks</li>
                    <li>Thumbnails</li>
                  </StudioCardsList>
                  <StudioCardsListTitle>
                    Non-applicable cards:
                  </StudioCardsListTitle>
                  <StudioCardsList>
                    <li>Music (separate selection here)</li>
                    <li>Cameras</li>
                    <li>Transitions</li>
                    <li>Memes / Reactions</li>
                    <li>Audio FX</li>
                    <li>On-kill FX</li>
                    <li>Signatures</li>
                  </StudioCardsList>
                </StudioCardsInfoDescription>
              </StudioCardsInfoContent>
            </StudioCardsInfoModal>
          )}

          <SectionDivider />
          <SectionTitle>Fragmovie Settings</SectionTitle>

          <SettingGroup>
            <SettingLabelRow>
              <SettingLabel>Song Selection</SettingLabel>
              {showSongError && (
                <SongErrorText>You must select a song</SongErrorText>
              )}
            </SettingLabelRow>
            <SongSelectButton onClick={() => setStep("song-selection")}>
              {selectedSong ? (
                <Flex alignItems="center" style={{ gap: 8 }}>
                  <FiMusic size={16} />
                  <span>{selectedSong.ui.title}</span>
                </Flex>
              ) : (
                <Flex alignItems="center" style={{ gap: 8 }}>
                  <FiMusic size={16} />
                  <span>Choose a song...</span>
                </Flex>
              )}
            </SongSelectButton>
          </SettingGroup>

          {/* Music volume slider - commented out as it's not used at the moment
        <SettingGroup>
          <SettingLabelRow>
            <SettingLabel>Song Volume</SettingLabel>
            <SettingValue>{songVolume}%</SettingValue>
          </SettingLabelRow>
          <SliderContainer>
            <Slider
              type="range"
              min={10}
              max={100}
              value={songVolume}
              onChange={(e) => setSongVolume(Number(e.target.value))}
            />
          </SliderContainer>
        </SettingGroup>
        */}

          <SettingGroup>
            <SettingLabelRow>
              <SettingLabel>
                Impact Intensity
                <AnimatedTooltip
                  content={
                    <>
                      When the kill and the music align just perfectly...
                      <br />
                      How dramatic should we make the slow motion effect?
                    </>
                  }
                  position="top"
                >
                  <FiInfo size={14} style={{ marginLeft: 4, cursor: "help" }} />
                </AnimatedTooltip>
              </SettingLabel>
              <SettingValue>
                {impactSeverity === 0
                  ? "No impact"
                  : impactSeverity === 100
                  ? "Super slowmo"
                  : `${impactSeverity}%`}
              </SettingValue>
            </SettingLabelRow>
            <SliderContainer>
              <SliderLabels>
                <span>No impact</span>
                <span>Super slowmo</span>
              </SliderLabels>
              <Slider
                type="range"
                min={0}
                max={100}
                value={impactSeverity}
                onChange={(e) => setImpactSeverity(Number(e.target.value))}
              />
            </SliderContainer>
          </SettingGroup>

          <SettingGroup>
            <ToggleRow>
              <ToggleLabel>
                <ToggleCheckbox
                  type="checkbox"
                  checked={speedupVfxEnabled}
                  onChange={(e) => setSpeedupVfxEnabled(e.target.checked)}
                  id="speedup-vfx"
                />
                <ToggleSlider />
                <ToggleLabelText>
                  Speedup VFX
                  <AnimatedTooltip
                    content="Adds a flash effect synced with the speed of the clip"
                    position="top"
                  >
                    <FiInfo
                      size={14}
                      style={{ marginLeft: 4, cursor: "help" }}
                    />
                  </AnimatedTooltip>
                </ToggleLabelText>
              </ToggleLabel>
            </ToggleRow>
          </SettingGroup>
        </SettingsSection>

        <ControlsSection>
          <Button
            classification="roundedFilledAlt"
            size="small"
            onClick={onBack}
            flex={true}
          >
            Back
          </Button>
          <CreateButton onClick={handleCreateTimeRemap} disabled={isCreating}>
            {isCreating ? (
              <LoadingSpinner height={16} width={16} />
            ) : (
              <>
                Create Instant Fragmovie
                {allFeaturesLocked && <StyledProBadge />}
              </>
            )}
          </CreateButton>
        </ControlsSection>
      </SettingsContainer>
      {subscriptionPaywallOpen && SubscriptionPaywallPortal}
    </>
  );
};

// Styled Components
const SettingsContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
`;

const HeaderSection = styled(BaseSection)`
  justify-content: space-between;
  padding: 24px;
  border-bottom: none;
`;

const StyledProBadge = styled(ProIcon)`
  width: 20px;
  height: 20px;
`;

const WhatIsThisButton = styled(Box)`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 4px;
  padding: 4px 8px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha10};
  }
`;

const PreviewModal = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`;

const PreviewContent = styled(Box)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  max-width: 500px;
  padding: 24px;
  position: relative;
`;

const PreviewCloseButton = styled(Box)`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-size: 24px;
  position: absolute;
  right: 12px;
  top: 12px;
`;

const PreviewVideoContainer = styled(Box)`
  cursor: pointer;
  margin-bottom: 16px;
  position: relative;
  width: 100%;
`;

const PreviewVideo = styled.video`
  border-radius: 8px;
  display: block;
  width: 100%;
`;

const PlayIconOverlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  height: 72px;
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: background 0.2s ease;
  width: 72px;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  svg {
    margin-left: 4px;
  }
`;

const PreviewDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.5;
`;

const SettingsSection = styled(Box)`
  padding: 0 24px 24px;
`;

const SectionDivider = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  height: 1px;
  margin: 16px 0;
`;

const SectionTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const SettingGroup = styled(Box)`
  margin-bottom: 16px;
`;

const SettingLabel = styled(Box)`
  color: ${({ theme }) => theme.colors.chalk};
  display: block;
  font-size: 12px;
  margin-bottom: 8px;
`;

const SettingLabelRow = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SettingValue = styled(Box)`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 600;
`;

const SongErrorText = styled(Box)`
  color: ${({ theme }) => theme.colors.error};
  font-size: 12px;
  font-weight: 500;
`;

const StyledInput = styled(InputText)`
  ${theme.text.labelSmall}
  background-color: ${({ theme }) => theme.colors.grey825};
  border: none;
  width: 100%;

  ::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
  }
`;

const SongSelectButton = styled(Box)`
  background: ${({ theme }) => theme.colors.grey825};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-size: 14px;
  padding: 12px 16px;
  text-align: left;
  width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const SliderContainer = styled(Box)`
  width: 100%;
`;

const SliderLabels = styled(Box)`
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  display: flex;
  font-size: 10px;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const Slider = styled.input`
  -webkit-appearance: none;
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 2px;
  height: 4px;
  outline: none;
  width: 100%;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    background: ${({ theme }) => theme.colors.envy};
    border-radius: 50%;
    cursor: pointer;
    height: 16px;
    width: 16px;
  }

  &::-moz-range-thumb {
    background: ${({ theme }) => theme.colors.envy};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    height: 16px;
    width: 16px;
  }
`;

const ToggleRow = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const ToggleLabel = styled.label`
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 8px;
`;

const ToggleCheckbox = styled.input`
  display: none;
`;

const ToggleSlider = styled.span`
  background: #253a49;
  border-radius: 10px;
  height: 20px;
  position: relative;
  transition: all 0.2s ease;
  width: 36px;
  flex-shrink: 0;

  &::after {
    background: #8fa9af;
    border-radius: 50%;
    content: "";
    height: 16px;
    left: 2px;
    position: absolute;
    top: 2px;
    transition: all 0.2s ease;
    width: 16px;
  }

  ${ToggleCheckbox}:checked + & {
    background: #4dbd94;
  }

  ${ToggleCheckbox}:checked + &::after {
    background: #0b1116;
    left: 18px;
  }
`;

const ToggleLabelText = styled(Box)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  font-size: 14px;
`;

const ToggleActions = styled(Box)`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const InfoIconButton = styled(Box)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const GoToStudioLink = styled.a`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  font-size: 12px;
  gap: 4px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledStudioIcon = styled(StudioBadgeIcon)`
  height: 16px;
  margin-right: 4px;
  width: 16px;
`;

const StudioCardsInfoModal = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`;

const StudioCardsInfoContent = styled(Box)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  max-height: 80vh;
  max-width: 500px;
  overflow-y: auto;
  padding: 24px;
  position: relative;
`;

const StudioCardsInfoDescription = styled(Box)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.6;

  p {
    margin-bottom: 12px;
  }
`;

const StudioCardsListTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  margin-top: 8px;
`;

const StudioCardsList = styled.ul`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 12px 0;
  padding-left: 20px;
`;

const ControlsSection = styled(Box)`
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 24px;
`;

const CreateButton = styled(Box)`
  align-items: center;
  background: linear-gradient(135deg, #00d4aa 0%, #00a080 100%);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  padding: 10px 20px;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// Song Selection Styles
const SongSelectionContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
`;

const SongSelectionHeader = styled(Box)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  display: flex;
  justify-content: left;
  padding: 24px;
`;

const FooterLeftSection = styled(Box)`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const SearchAndFilterRow = styled(Box)`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
`;

const SearchInput = styled(InputText)`
  ${theme.text.labelSmall}
  background-color: ${({ theme }) => theme.colors.grey825};
  border: none;
  flex: 1;

  ::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
  }
`;

const ThemeFilterSelect = styled.select`
  background: ${({ theme }) => theme.colors.grey825};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  font-size: 14px;
  padding: 8px 12px;

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }

  ::-webkit-scrollbar {
    width: 25px;
  }
  ::-webkit-scrollbar-corner {
    background: ${({ theme }) => theme.colors.ned};
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.ned};
    background-clip: content-box;
    border: 5px solid transparent;
    border-radius: 999px;
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grey825};
  }
`;

const SongsGridWrapper = styled(Box)`
  flex: 1;
  max-height: 300px;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 25px;
  }
  ::-webkit-scrollbar-corner {
    background: ${({ theme }) => theme.colors.ned};
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.ned};
    background-clip: content-box;
    border: 5px solid transparent;
    border-radius: 999px;
    height: 50px;
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grey825};
  }
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const NoSongsText = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  padding: 40px;
  text-align: center;
`;

const SongSelectionFooter = styled(Box)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
`;

const SelectionIndicator = styled(Box)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-size: 13px;
  gap: 4px;
`;

const SelectedSongTitle = styled(Box)`
  color: ${({ theme }) => theme.colors.envy};
  font-weight: 600;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoSelectionText = styled(Box)`
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  font-style: italic;
`;
